/**
 * VoicePreviewButton — tiny "play sample" button that synthesises a
 * 1-line preview of a TTS voice via `/api/tts/generate`. Lets admins
 * and writers audition a voice before committing to a full episode.
 *
 * State machine: idle → loading → playing → idle (auto on `ended`).
 * Caches the resulting URL on the component instance so a second tap
 * skips the round-trip to the TTS server.
 *
 * Props:
 *   • voice   (str)  — the voice id sent to the TTS service.
 *   • sample  (str)  — optional override of the demo line.
 *   • size    ("xs"|"sm") — visual scale.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_SAMPLE = (voice) =>
  `Hi, I'm ${voice}. I'm ready to narrate your story.`;

const VoicePreviewButton = ({ voice, sample, size = 'sm', className = '' }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);
  const cachedUrlRef = useRef(null);

  useEffect(() => {
    // Reset cache + stop any in-flight preview when the voice changes.
    cachedUrlRef.current = null;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* ignore */ }
      audioRef.current = null;
    }
    setStatus('idle');
  }, [voice]);

  const stopPlayback = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* ignore */ }
      audioRef.current.currentTime = 0;
    }
    setStatus('idle');
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === 'playing') {
      stopPlayback();
      return;
    }
    if (status === 'loading') return;

    // Cached URL → just replay.
    if (cachedUrlRef.current) {
      const a = new Audio(cachedUrlRef.current);
      audioRef.current = a;
      a.addEventListener('ended', () => setStatus('idle'));
      a.addEventListener('error', () => setStatus('idle'));
      setStatus('playing');
      a.play().catch(() => setStatus('idle'));
      return;
    }

    setStatus('loading');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sample || DEFAULT_SAMPLE(voice || 'this voice'),
          voice: voice || 'default',
          language: 'en',
          content_id: '',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      const url = data.url || data.audio_url;
      if (!url) throw new Error('TTS response missing url');
      cachedUrlRef.current = url;
      const a = new Audio(url);
      audioRef.current = a;
      a.addEventListener('ended', () => setStatus('idle'));
      a.addEventListener('error', () => setStatus('idle'));
      setStatus('playing');
      a.play().catch(() => setStatus('idle'));
    } catch (err) {
      setStatus('idle');
      toast.error(`Preview failed: ${err.message || 'TTS error'}`);
    }
  };

  const sz = size === 'xs'
    ? 'w-5 h-5'
    : 'w-6 h-6';
  const icon = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3';

  return (
    <button
      type="button"
      onClick={handleClick}
      title={status === 'playing' ? 'Stop preview' : `Preview "${voice}" voice`}
      aria-label={`Preview voice ${voice}`}
      className={`shrink-0 inline-flex items-center justify-center ${sz} rounded-full bg-white/15 hover:bg-white/25 text-white transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300 ${className}`}
      data-testid={`voice-preview-${(voice || 'default').replace(/\s+/g, '-')}`}
    >
      {status === 'loading' ? (
        <Loader2 className={`${icon} animate-spin`} />
      ) : status === 'playing' ? (
        <Pause className={icon} />
      ) : (
        <Play className={icon} />
      )}
    </button>
  );
};

export default VoicePreviewButton;
